import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { X, Users, AlertTriangle, Send, ExternalLink, Copy as CopyIcon, ChevronDown, ChevronUp, UserMinus, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

// Conservative ceiling — most mail clients truncate URLs past this.
const MAILTO_CHAR_LIMIT = 1900;

/**
 * Bulk Email composer used by Admin and Partner dashboards.
 * Two modes:
 *   - Automation ON  → sends via SendGrid personalization (privacy-safe individual emails).
 *   - Automation OFF → opens the user's email app via a mailto: link (BCC). When
 *                      recipient count exceeds mailto limits, offers a copy-list.
 *
 * Props:
 *   isOpen, onClose
 *   scope  — 'admin' or 'partner'  (changes the endpoints used)
 *   partnerOrgId  — required for scope='partner'
 *   prefill        — optional { subject, body_html, filter_type, filter_value }
 */
const BulkEmailModal = ({ isOpen, onClose, scope = 'admin', partnerOrgId = null, prefill = null }) => {
  const [form, setForm] = useState({ subject: '', body_html: '', recipient_kind: 'participants', filter_type: 'all', filter_value: '' });
  const [recipients, setRecipients] = useState({ total: 0, sample: [], full: [] });
  const [loadingCount, setLoadingCount] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [automationEnabled, setAutomationEnabled] = useState(true);
  // Inline preview/remove state
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [removedEmails, setRemovedEmails] = useState(new Set());

  // Options for dynamic filter value pickers
  const [towns, setTowns] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const VOLUNTEER_LEVELS = ['level_1', 'level_2', 'level_3'];
  const CLEARANCE_STATUSES = ['not_started', 'pending', 'approved', 'rejected'];
  const HOURS_STATUSES = [
    { value: 'pending', label: 'Pending hour requests' },
    { value: 'completed', label: 'Has completed hours' },
    { value: 'none', label: 'No hours yet' },
  ];
  // Partner-side town set (built from orgs' towns_served on fetchMeta).
  const [partnerTowns, setPartnerTowns] = useState([]);

  // Partner admins never see the kind toggle — they only email their own participants.
  const kindPickerVisible = scope === 'admin';

  const base = scope === 'partner' ? `${API}/partner/bulk-email` : `${API}/admin/bulk-email`;

  const fetchMeta = useCallback(async () => {
    try {
      const [usersRes, orgsRes, oppsRes, statusRes] = await Promise.all([
        axios.get(`${API}/admin/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/partner-organizations`).catch(() => ({ data: [] })),
        axios.get(`${API}/opportunities?view=all`).catch(() => ({ data: [] })),
        axios.get(`${API}/email/status`).catch(() => ({ data: { email_enabled: false, automation_toggle: false } })),
      ]);
      const townSet = new Set((usersRes.data || []).map(u => u.town).filter(Boolean));
      setTowns([...townSet].sort());
      const oList = (orgsRes.data || []);
      setOrgs(scope === 'partner' ? oList.filter(o => o.id === partnerOrgId) : oList);
      // Aggregate towns_served across all orgs for the partner-town filter.
      const partnerTownSet = new Set();
      oList.forEach(o => (o.towns_served || []).forEach(t => t && partnerTownSet.add(t)));
      setPartnerTowns([...partnerTownSet].sort());
      const oppList = (oppsRes.data || []);
      setOpportunities(scope === 'partner' ? oppList.filter(o => o.partner_org_id === partnerOrgId) : oppList);
      // Drive Send-button visibility off the automation toggle alone so admins
      // can still exercise the SendGrid path in mock mode (no key configured).
      setAutomationEnabled(!!statusRes.data.automation_toggle);
    } catch (e) {
      // best-effort
    }
  }, [scope, partnerOrgId]);

  const refreshCount = useCallback(async () => {
    setLoadingCount(true);
    try {
      const params = new URLSearchParams({
        filter_type: form.filter_type,
        recipient_kind: form.recipient_kind || 'participants',
        full: 'true',
      });
      if (form.filter_value) params.set('filter_value', form.filter_value);
      const res = await axios.get(`${base}/recipients?${params.toString()}`);
      const full = res.data.recipients || res.data.sample || [];
      setRecipients({ total: res.data.total || 0, sample: res.data.sample || [], full });
      // Whenever the filter changes, reset any individual exclusions.
      setRemovedEmails(new Set());
    } catch (err) {
      console.error('refreshCount failed', err);
      setRecipients({ total: 0, sample: [], full: [] });
    } finally {
      setLoadingCount(false);
    }
  }, [base, form.filter_type, form.filter_value, form.recipient_kind]);

  useEffect(() => {
    if (isOpen) {
      setResult(null);
      setForm({
        subject: '', body_html: '', recipient_kind: 'participants', filter_type: 'all', filter_value: '',
        ...(prefill || {}),
      });
      setRemovedEmails(new Set());
      setShowRecipientPicker(false);
      fetchMeta();
    }
  }, [isOpen, fetchMeta, prefill]);

  useEffect(() => {
    if (isOpen) refreshCount();
  }, [isOpen, refreshCount]);

  // Effective recipient list — full set minus the ones the admin removed.
  const effectiveRecipients = useMemo(() => {
    const full = recipients.full || recipients.sample || [];
    if (removedEmails.size === 0) return full;
    return full.filter(r => !removedEmails.has((r.email || '').toLowerCase()));
  }, [recipients, removedEmails]);

  const effectiveTotal = effectiveRecipients.length;
  const isPartialSelection = removedEmails.size > 0;

  const buildMailtoUrl = (recipientList, subject, body) => {
    // BCC protects privacy — recipients won't see each other's addresses.
    const bccList = recipientList.map(r => r.email).filter(Boolean).join(',');
    const params = new URLSearchParams();
    if (bccList) params.set('bcc', bccList);
    if (subject) params.set('subject', subject);
    if (body) params.set('body', body);
    return `mailto:?${params.toString()}`;
  };

  const openInEmailApp = async () => {
    if (!form.subject.trim() && !form.body_html.trim() && effectiveTotal === 0) {
      toast.error('Fill in at least a subject or body, or pick a filter with recipients');
      return;
    }
    if (effectiveTotal === 0) {
      toast.error('No recipients selected');
      return;
    }
    try {
      // Persist the bulk-email log even though no SendGrid call happens, so
      // the admin can later see this entry in Email Logs.
      const payload = {
        ...form,
        recipient_emails: isPartialSelection ? effectiveRecipients.map(r => r.email) : null,
      };
      await axios.post(base, payload).catch(err => { console.error('bulk-email log failed', err); });

      const list = effectiveRecipients;
      const plainBody = form.body_html.replace(/<[^>]+>/g, '').trim();
      const url = buildMailtoUrl(list, form.subject, plainBody);
      if (url.length > MAILTO_CHAR_LIMIT) {
        // Too many recipients — show a copyable list instead
        setResult({
          disabled: true,
          oversize: true,
          recipients: list,
          subject: form.subject,
          body: plainBody,
        });
        toast('Recipient list is too large for a mailto link — copy the addresses below.', { duration: 5000 });
        return;
      }
      window.location.href = url;
      setResult({ disabled: true, opened: true, recipients: list });
      toast.success(`Opened in your email app (${list.length} recipient${list.length === 1 ? '' : 's'} in BCC)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to prepare email');
    }
  };

  const copyRecipientsToClipboard = async (list) => {
    try {
      const addresses = list.map(r => r.email).filter(Boolean).join(', ');
      await navigator.clipboard.writeText(addresses);
      toast.success(`Copied ${list.length} email address${list.length === 1 ? '' : 'es'}`);
    } catch (e) {
      toast.error('Unable to copy — select and copy manually');
    }
  };

  const send = async () => {
    if (!form.subject.trim() || !form.body_html.trim()) {
      toast.error('Subject and message body are required');
      return;
    }
    // Filters that don't require a filter_value (they're self-contained)
    const selfContained = new Set(['all', 'active', 'active_opportunities', 'pending_approvals']);
    if (!selfContained.has(form.filter_type) && !form.filter_value) {
      toast.error('Pick a value for this filter');
      return;
    }
    if (effectiveTotal === 0) {
      toast.error('No recipients selected');
      return;
    }
    if (!window.confirm(`Send this email to ${effectiveTotal} recipient(s)?\n\nEach person will receive their own individual email — addresses stay private.`)) return;
    setSending(true);
    setResult(null);
    try {
      const payload = {
        ...form,
        recipient_emails: isPartialSelection ? effectiveRecipients.map(r => r.email) : null,
      };
      const res = await axios.post(base, payload);
      setResult(res.data);
      if (res.data.disabled) {
        toast('Email automation is off — use the "Open in Email App" button below.', { duration: 5000 });
      } else if (res.data.sent > 0) {
        toast.success(`Sent to ${res.data.sent} of ${res.data.attempted}`);
      } else if (res.data.mocked > 0) {
        toast.success(`Mock-sent ${res.data.mocked} email(s) (SendGrid not configured — check Email Logs)`);
      } else {
        toast.error(`Failed — see details below`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="bulk-email-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E9DDCF] flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-2xl font-bold text-[#222]">Send Bulk Email</h2>
            <p className="text-sm text-[#666] mt-1">
              Each recipient gets their own individual email. Addresses stay private — no BCC, no one sees anyone else.
            </p>
          </div>
          <button onClick={onClose} className="text-[#666] hover:text-[#222]" data-testid="close-bulk-email">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!automationEnabled && (
            <div
              className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-2"
              data-testid="email-automation-disabled-banner"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Email automation is currently disabled. Use your email app to send messages.</p>
                <p className="text-xs mt-1">Pick a filter and click <em>Open in Email App</em> below — your default mail client will open with everyone in BCC and your message pre-filled.</p>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Recipients</label>
            {kindPickerVisible && (
              <div className="flex gap-2 mb-2" data-testid="bulk-recipient-kind">
                {[
                  { value: 'participants', label: 'Participants' },
                  { value: 'partners', label: 'Partners / Orgs' },
                  { value: 'both', label: 'Both' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, recipient_kind: opt.value, filter_type: 'all', filter_value: '' }))}
                    className={`flex-1 px-3 py-1.5 rounded-lg border text-sm font-medium ${
                      form.recipient_kind === opt.value
                        ? 'bg-[#8B2E3C] text-white border-[#8B2E3C]'
                        : 'bg-white text-[#444] border-[#E9DDCF] hover:border-[#8B2E3C]'
                    }`}
                    data-testid={`bulk-kind-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {form.recipient_kind === 'partners' ? (
                <select
                  className="input-field"
                  value={form.filter_type}
                  onChange={(e) => setForm(f => ({ ...f, filter_type: e.target.value, filter_value: '' }))}
                  data-testid="bulk-filter-type"
                >
                  <option value="all">All partners / organizations</option>
                  <option value="organization">Individual partner / org</option>
                  <option value="town">By town served</option>
                  <option value="active_opportunities">Partners with active opportunities</option>
                  <option value="pending_approvals">Partners with pending volunteer approvals</option>
                </select>
              ) : form.recipient_kind === 'both' ? (
                <select
                  className="input-field"
                  value={form.filter_type}
                  onChange={(e) => setForm(f => ({ ...f, filter_type: e.target.value, filter_value: '' }))}
                  data-testid="bulk-filter-type"
                >
                  <option value="all">Everyone (all participants + all partners)</option>
                  <option value="town">By town</option>
                  <option value="organization">By organization</option>
                </select>
              ) : (
                <select
                  className="input-field"
                  value={form.filter_type}
                  onChange={(e) => setForm(f => ({ ...f, filter_type: e.target.value, filter_value: '' }))}
                  data-testid="bulk-filter-type"
                >
                  <option value="all">All participants</option>
                  <option value="active">Active (signed up in last 90 days)</option>
                  <option value="town">By town</option>
                  <option value="organization">By organization (signed up for its opps)</option>
                  <option value="opportunity">By specific opportunity</option>
                  <option value="volunteer_level">By volunteer level</option>
                  <option value="clearance_status">By clearance status</option>
                  <option value="hours_status">By hours status</option>
                </select>
              )}
              {form.filter_type === 'town' && (
                <select className="input-field" value={form.filter_value} onChange={(e) => setForm(f => ({ ...f, filter_value: e.target.value }))} data-testid="bulk-filter-value">
                  <option value="">— Pick a town —</option>
                  {(form.recipient_kind === 'partners' ? partnerTowns : towns).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
              {form.filter_type === 'organization' && (
                <select className="input-field" value={form.filter_value} onChange={(e) => setForm(f => ({ ...f, filter_value: e.target.value }))} data-testid="bulk-filter-value">
                  <option value="">— Pick an organization —</option>
                  {orgs.map(o => <option key={o.id} value={o.id}>{o.org_name}</option>)}
                </select>
              )}
              {form.filter_type === 'opportunity' && (
                <select className="input-field" value={form.filter_value} onChange={(e) => setForm(f => ({ ...f, filter_value: e.target.value }))} data-testid="bulk-filter-value">
                  <option value="">— Pick an opportunity —</option>
                  {opportunities.map(o => <option key={o.id} value={o.id}>{o.title} ({o.date})</option>)}
                </select>
              )}
              {form.filter_type === 'volunteer_level' && (
                <select className="input-field" value={form.filter_value} onChange={(e) => setForm(f => ({ ...f, filter_value: e.target.value }))} data-testid="bulk-filter-value">
                  <option value="">— Pick a level —</option>
                  {VOLUNTEER_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              )}
              {form.filter_type === 'clearance_status' && (
                <select className="input-field" value={form.filter_value} onChange={(e) => setForm(f => ({ ...f, filter_value: e.target.value }))} data-testid="bulk-filter-value">
                  <option value="">— Pick a status —</option>
                  {CLEARANCE_STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              )}
              {form.filter_type === 'hours_status' && (
                <select className="input-field" value={form.filter_value} onChange={(e) => setForm(f => ({ ...f, filter_value: e.target.value }))} data-testid="bulk-filter-value">
                  <option value="">— Pick hours status —</option>
                  {HOURS_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              )}
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-[#666] flex items-center gap-2" data-testid="bulk-recipient-count">
                <Users className="w-3.5 h-3.5" />
                {loadingCount
                  ? 'Counting…'
                  : isPartialSelection
                    ? `${effectiveTotal} of ${recipients.total} selected · ${removedEmails.size} removed`
                    : `${effectiveTotal} recipient${effectiveTotal === 1 ? '' : 's'} will receive this email`}
              </p>
              {(recipients.full || []).length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowRecipientPicker(v => !v)}
                  className="text-xs text-[#8B2E3C] hover:underline flex items-center gap-1 font-medium"
                  data-testid="bulk-toggle-picker"
                >
                  {showRecipientPicker ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showRecipientPicker ? 'Hide list' : 'Edit recipients'}
                </button>
              )}
            </div>

            {showRecipientPicker && (recipients.full || []).length > 0 && (
              <div className="mt-2 border border-[#E9DDCF] rounded-lg" data-testid="bulk-recipient-picker">
                <div className="flex items-center justify-between px-3 py-2 bg-[#FBF4EA] border-b border-[#E9DDCF] text-xs">
                  <span className="text-[#444] font-medium">Recipient list</span>
                  {isPartialSelection && (
                    <button
                      type="button"
                      onClick={() => setRemovedEmails(new Set())}
                      className="text-[#8B2E3C] hover:underline flex items-center gap-1"
                      data-testid="bulk-restore-all-button"
                    >
                      <RotateCcw className="w-3 h-3" /> Restore all
                    </button>
                  )}
                </div>
                <ul className="max-h-56 overflow-y-auto divide-y divide-[#F0E5D6]">
                  {(recipients.full || []).map(r => {
                    const removed = removedEmails.has((r.email || '').toLowerCase());
                    return (
                      <li
                        key={r.email}
                        className={`flex items-center justify-between gap-2 px-3 py-1.5 text-sm ${removed ? 'opacity-50 bg-[#FAFAFA]' : ''}`}
                        data-testid={`bulk-recipient-row-${r.email}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className={`text-[#222] truncate ${removed ? 'line-through' : ''}`}>{r.name || r.email}</div>
                          {r.name && <div className="text-xs text-[#888] truncate">{r.email}</div>}
                        </div>
                        <button
                          type="button"
                          onClick={() => setRemovedEmails(s => {
                            const next = new Set(s);
                            const k = (r.email || '').toLowerCase();
                            if (next.has(k)) next.delete(k); else next.add(k);
                            return next;
                          })}
                          className={`text-xs px-2 py-1 rounded font-medium flex items-center gap-1 shrink-0 ${
                            removed
                              ? 'border border-[#8B2E3C] text-[#8B2E3C] hover:bg-[#FBF4EA]'
                              : 'text-[#888] hover:text-red-600 hover:bg-red-50'
                          }`}
                          data-testid={`bulk-toggle-recipient-${r.email}`}
                          title={removed ? 'Restore this recipient' : 'Remove from list'}
                        >
                          {removed ? (<><RotateCcw className="w-3 h-3" /> Restore</>) : (<><UserMinus className="w-3 h-3" /> Remove</>)}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Subject *</label>
            <input
              type="text"
              className="input-field"
              value={form.subject}
              onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="e.g., Upcoming community event this Saturday"
              data-testid="bulk-subject"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Message *</label>
            <textarea
              className="input-field"
              rows={8}
              value={form.body_html}
              onChange={(e) => setForm(f => ({ ...f, body_html: e.target.value }))}
              placeholder="Write your message here. HTML is supported."
              data-testid="bulk-body"
            />
            <p className="text-xs text-[#666] mt-1">HTML is supported. Line breaks are preserved.</p>
          </div>

          {result && (
            <div className={`p-3 rounded-lg text-sm ${result.sent > 0 ? 'bg-green-50 text-green-900 border border-green-200' : (result.disabled ? 'bg-amber-50 text-amber-900 border border-amber-200' : 'bg-amber-50 text-amber-900 border border-amber-200')}`} data-testid="bulk-send-result">
              {result.disabled ? (
                <>
                  <p className="font-medium">Email automation is off — nothing was sent by the server.</p>
                  <p className="mt-1">{result.attempted || (result.recipients || []).length} recipient{((result.attempted || (result.recipients || []).length) === 1) ? '' : 's'} identified. Use the button below to open your email app.</p>
                  {result.oversize && (
                    <div className="mt-3 space-y-2" data-testid="bulk-copy-fallback">
                      <p className="text-xs">The list is too long for a <code>mailto:</code> link. Copy the addresses below and paste into your email client's BCC field instead.</p>
                      <textarea
                        readOnly
                        className="w-full text-xs p-2 bg-white border border-amber-200 rounded font-mono max-h-32"
                        rows={5}
                        value={(result.recipients || []).map(r => r.email).join(', ')}
                        data-testid="bulk-copy-textarea"
                      />
                      <button
                        onClick={() => copyRecipientsToClipboard(result.recipients || [])}
                        className="btn-outline flex items-center gap-2 text-sm"
                        data-testid="bulk-copy-addresses-button"
                      >
                        <CopyIcon className="w-4 h-4" /> Copy {(result.recipients || []).length} address{((result.recipients || []).length === 1) ? '' : 'es'}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p><strong>Attempted:</strong> {result.attempted} · <strong>Sent:</strong> {result.sent} · <strong>Failed:</strong> {result.failed}{result.mocked > 0 && <> · <strong>Mocked:</strong> {result.mocked}</>}</p>
                  {result.errors && result.errors.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer font-medium">{result.errors.length} error(s) — click to see</summary>
                      <ul className="mt-1 text-xs space-y-0.5 max-h-48 overflow-y-auto">
                        {result.errors.slice(0, 20).map((er, i) => (
                          <li key={i} className="break-all">{er.email}: {er.error}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-[#E9DDCF] flex flex-wrap justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-outline" disabled={sending}>Close</button>
          <button
            onClick={openInEmailApp}
            className={automationEnabled ? 'btn-outline flex items-center gap-2' : 'btn-primary flex items-center gap-2'}
            disabled={sending || effectiveTotal === 0}
            data-testid="bulk-open-in-email-app-button"
            title="Opens your default email client with all recipients in BCC"
          >
            <ExternalLink className="w-4 h-4" />
            Open in Email App
          </button>
          {automationEnabled && (
            <button
              onClick={send}
              className="btn-primary flex items-center gap-2"
              disabled={sending || effectiveTotal === 0}
              data-testid="bulk-send-button"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending…' : `Send to ${effectiveTotal} recipient${effectiveTotal === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkEmailModal;
